import React, { useState } from 'react';
import { Card, CardHeader, CardContent, TextField, Button, Box, Typography, LinearProgress, IconButton, InputAdornment, Alert } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockResetIcon from '@mui/icons-material/LockReset';
import api from '../../services/api';
import { useAppTheme } from '../../hooks/useAppTheme';

/**
 * F26 (FUTURE_ENHANCEMENTS) — strength scorer mirrors the backend policy
 * enforced by validatePasswordPolicy + the Zod registerSchema: min 12 chars
 * + 3 of (lowercase, UPPERCASE, digit, symbol). Common-password short-list
 * caps the score at 0 so the bar can never look "Strong" for the obvious
 * dictionary hits.  The real gate is still on the server.
 */
const COMMON_PWS = new Set(['password', 'password123', '12345678', 'qwerty', 'letmein', 'welcome', 'admin']);
function scorePassword(pw: string): { score: number; label: string; color: 'error' | 'warning' | 'info' | 'success' } {
  if (!pw) return { score: 0, label: '—', color: 'error' };
  if (COMMON_PWS.has(pw.toLowerCase())) return { score: 0, label: 'Too common', color: 'error' };
  let s = 0;
  if (pw.length >= 12) s++;
  if (pw.length >= 16) s++;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter(re => re.test(pw)).length;
  if (classes >= 3) s++;
  if (classes === 4) s++;
  const labels = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];
  const colors: Array<'error' | 'warning' | 'info' | 'success'> = ['error', 'error', 'warning', 'info', 'success'];
  const idx = Math.min(4, s);
  return { score: (idx / 4) * 100, label: labels[idx], color: colors[idx] };
}
/** Server-side validatePasswordPolicy mirror — true when the password is
 *  acceptable. Used to gate the submit button so the user sees a 400
 *  validation error less often. */
function meetsPolicy(pw: string): boolean {
  if (pw.length < 12) return false;
  return [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter(re => re.test(pw)).length >= 3;
}

const PasswordChangeCard: React.FC = () => {
  const { C } = useAppTheme();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const strength = scorePassword(newPw);
  const mismatch = confirmPw.length > 0 && confirmPw !== newPw;
  const canSubmit = !!currentPw && meetsPolicy(newPw) && newPw === confirmPw && newPw !== currentPw;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await api.post<any>('/auth/change-password', { currentPassword: currentPw, newPassword: newPw });
      if (r.data?.success) {
        setSuccess(r.data?.message || 'Password changed. Other sessions have been signed out.');
        setCurrentPw(''); setNewPw(''); setConfirmPw('');
      } else {
        setError(r.data?.error?.message || 'Failed to change password');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockResetIcon fontSize="small" />
            <span>Change Password</span>
          </Box>
        }
        subheader="Other active sessions will be signed out after a successful change."
      />
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 480 }}>
          <TextField
            label="Current password"
            type={showCurrent ? 'text' : 'password'}
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            size="small"
            fullWidth
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowCurrent(v => !v)} edge="end">
                    {showCurrent ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="New password"
            type={showNew ? 'text' : 'password'}
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            size="small"
            fullWidth
            autoComplete="new-password"
            helperText="Min 12 chars, 3 of: lowercase / UPPERCASE / digit / symbol."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowNew(v => !v)} edge="end">
                    {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {newPw.length > 0 && (
            <Box>
              <LinearProgress variant="determinate" value={strength.score} color={strength.color} sx={{ height: 6, borderRadius: 3 }} />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                Strength: <strong>{strength.label}</strong>
              </Typography>
            </Box>
          )}
          <TextField
            label="Confirm new password"
            type={showNew ? 'text' : 'password'}
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            size="small"
            fullWidth
            autoComplete="new-password"
            error={mismatch}
            helperText={mismatch ? "Passwords don't match" : ' '}
          />
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              sx={{ minWidth: 160 }}
            >
              {submitting ? 'Changing…' : 'Change password'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PasswordChangeCard;
